import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import type {
  RaidState, RaidFocusKind, RaidKeepPick, RaidStatusResponse, RaidNoneResponse,
} from './raid-state.types';

/*
 * Region DATA deliberately does NOT live here — `world-map-state.ts` owns it.
 *
 * It used to live in both, and that caused a launch to silently no-op: the map
 * populated `worldMapStore` while the focus buttons read
 * `raidStateStore.selectedRegion`, which only `loadRegions()` (called from the
 * old separate raid panel) ever set. Opening the map and clicking a focus hit
 * `if (!target) return`. Two stores for one concept, so the two could disagree —
 * and did.
 *
 * What stays here is the launch INTENT: which region the player has chosen. The
 * map writes it via `selectRegion`.
 */

const INITIAL_STATE: RaidState = {
  raidId: null,
  status: null,
  focus: null,
  districtId: null,
  districtTier: 0,
  step: 0,
  stepsPerHour: 15,
  daylightBudget: 24,
  stepDurationMs: 60_000,
  startedAt: 0,
  hp: 0,
  maxHp: 0,
  bag: [],
  items: {},
  bagWeight: 0,
  carryWeightCap: 0,
  steps: [],
  outcome: null,
  rulesVersion: 1,
  choosing: false,
  room: null,
  carriedSlotCap: 0,
  bagSlots: 0,
  clockOffsetMs: 0,
  lastPollAt: 0,
  pollIntervalMs: 2000,
  isPolling: false,
  error: null,
  selectedRegion: null,
};

export const raidStateStore = createStore<RaidState>({ ...INITIAL_STATE });

/**
 * The fields every raid response carries that change while a raid runs. One
 * mapper for the start, the restore, the poll and every action, so a field
 * added to the view cannot be forgotten by one of them (rules 2's `choosing`
 * has to survive a poll, or the picker would vanish 2 s after it opened).
 */
function liveFields(r: RaidStatusResponse): Partial<RaidState> {
  const serverNow = Date.parse(r.now);
  return {
    status: r.status,
    step: r.step,
    hp: r.hp,
    bag: r.bag,
    items: r.items ?? {},
    bagWeight: r.bagWeight,
    steps: r.steps,
    outcome: r.outcome,
    rulesVersion: r.rulesVersion ?? 1,
    choosing: r.choosing ?? false,
    room: r.room ?? null,
    carriedSlotCap: r.carriedSlotCap ?? 0,
    bagSlots: r.bagSlots ?? 0,
    clockOffsetMs: Number.isFinite(serverNow) ? serverNow - Date.now() : 0,
    error: null,
  };
}

export function selectRegion(key: string): void {
  raidStateStore.set((s) => ({ ...s, selectedRegion: key }));
}

export async function startRaid(focus: RaidFocusKind, districtId: string): Promise<void> {
  raidStateStore.set((s) => ({ ...s, error: null }));
  try {
    const response = await api.post<RaidStatusResponse>('/raid/start', {
      focus,
      districtId,
    });
    raidStateStore.set((s) => ({
      ...s,
      raidId: response.raidId,
      status: response.status,
      focus: response.focus,
      districtId: response.districtId,
      districtTier: response.districtTier,
      stepsPerHour: response.stepsPerHour ?? 1,
      daylightBudget: response.daylightBudget,
      stepDurationMs: response.stepDurationMs,
      startedAt: parseStart(response.startedAt),
      maxHp: response.maxHp,
      carryWeightCap: response.carryWeightCap,
      ...liveFields(response),
      outcome: null,
      lastPollAt: Date.now(),
      pollIntervalMs: s.pollIntervalMs,
      isPolling: true,
    }));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to start raid';
    raidStateStore.set((s) => ({ ...s, error: message }));
    throw err;
  }
}

/**
 * True from the instant a poll request is issued until its response settles.
 *
 * Module-local rather than a store field on purpose: `resetRaid()` is called
 * from INSIDE the poll (the "server says no raid" branch) and replaces the
 * whole state object, so a flag living in the store would be wiped while its
 * own request was still in flight.
 */
let pollInFlight = false;

/**
 * One `GET /raid/current`, throttled to `pollIntervalMs`.
 *
 * ─── Why the window is stamped BEFORE the request, not after ───────────────
 * This is called from the render loop at ~60 Hz (index.ts -> updateRaidPolling).
 * `lastPollAt` used to be written only once the response came back, so every
 * frame between issuing a request and receiving it ALSO passed the throttle
 * and issued its own. At 60 fps a 200 ms round trip meant ~12 requests per
 * intended poll, and the effect compounds: the extra load slows responses,
 * which widens the window, which fires more requests. The global limiter is
 * 120 req/min per IP, so a live raid with the map open reliably produced a
 * burst of 429s — the bug Janeks reported as "while on map we get 429,
 * because of multiple calls".
 *
 * Stamping first closes the window immediately; `pollInFlight` additionally
 * covers a response slower than the interval itself, where stamping alone
 * would still permit one overlapping request per interval.
 *
 * The stamp is deliberately NOT rolled back on failure — a failing poll should
 * wait out the interval like a successful one rather than retrying every frame,
 * which is exactly how a 429 turns into a self-sustaining flood.
 */
/**
 * Is the player "out on a raid" as far as the screen is concerned?
 *
 * True while the run is live AND while its result is still waiting for the
 * player to press Continue. The second half matters: the instant status leaves
 * ACTIVE the raid is over server-side, but the player has not seen what they
 * brought home yet, and dropping them back into the settlement before they have
 * would bury the result under a city they were not standing in a moment ago.
 *
 * One predicate, read by the settlement view (hide the world, lock input) and
 * by the map (refuse to close), so the two cannot disagree about when the raid
 * screen ends.
 */
export function raidLocksWorld(s: RaidState = raidStateStore.get()): boolean {
  return s.status === 'ACTIVE' || (s.raidId !== null && s.outcome !== null);
}

/**
 * One-shot restore of a raid already in progress.
 *
 * `startRaid` is the only other writer of an ACTIVE raid and `pollRaidStatus`
 * does nothing unless the store is already ACTIVE, so a page reload mid-raid
 * left the client believing there was no raid at all: the player was dropped
 * into the settlement while their run kept ticking on the server. The server
 * already documents `GET /raid/current` as "the login/mid-run sync".
 *
 * Failure is swallowed on purpose. This runs at mount, and a flaky request must
 * not stop the settlement from loading; the worst case is the old behaviour.
 */
export async function loadCurrentRaid(): Promise<void> {
  try {
    const response = await api.get<RaidStatusResponse | RaidNoneResponse>('/raid/current');
    if (!('raidId' in response) || response.raidId === null) {
      if (raidStateStore.get().status !== null) resetRaid();
      return;
    }
    raidStateStore.set((s) => ({
      ...s,
      raidId: response.raidId,
      focus: response.focus,
      districtId: response.districtId,
      districtTier: response.districtTier,
      stepsPerHour: response.stepsPerHour ?? 1,
      daylightBudget: response.daylightBudget,
      stepDurationMs: response.stepDurationMs,
      startedAt: parseStart(response.startedAt),
      maxHp: response.maxHp,
      carryWeightCap: response.carryWeightCap,
      ...liveFields(response),
      lastPollAt: Date.now(),
      isPolling: response.status === 'ACTIVE',
    }));
  } catch {
    /* see above */
  }
}

export async function pollRaidStatus(): Promise<void> {
  const state = raidStateStore.get();

  // Throttle polling
  if (pollInFlight || state.status !== 'ACTIVE'
    || Date.now() - state.lastPollAt < state.pollIntervalMs) {
    return;
  }

  pollInFlight = true;
  raidStateStore.set((s) => ({ ...s, lastPollAt: Date.now() }));

  try {
    const response = await api.get<RaidStatusResponse | RaidNoneResponse>('/raid/current');

    if (!('raidId' in response) || response.raidId === null) {
      resetRaid();
      return;
    }

    // Update state from server response
    raidStateStore.set((s) => ({
      ...s,
      ...liveFields(response),
      lastPollAt: Date.now(),
    }));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to poll raid status';
    // A poll that keeps failing reports the SAME error every 2 s. Say it once:
    // an identical message leaves the state alone (no repaint, no re-announce),
    // and the next successful poll clears it.
    if (raidStateStore.get().error !== message) raidStateStore.set((s) => ({ ...s, error: message }));
  } finally {
    pollInFlight = false;
  }
}

/**
 * Drops one carried find mid-raid. The server records it against the raid and
 * answers with the new state, so the bag the player sees is the bag it replays.
 */
export async function discardRaidItem(itemId: string): Promise<void> {
  raidStateStore.set((s) => ({ ...s, error: null }));
  try {
    const response = await api.post<RaidStatusResponse>('/raid/discard', { itemId });
    raidStateStore.set((s) => ({ ...s, ...liveFields(response) }));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to discard';
    raidStateStore.set((s) => ({ ...s, error: message }));
    throw err;
  }
}

/**
 * Extract. Under rules 2 this freezes the run and opens the choice of what to
 * carry (`choosing`); the raid stays ACTIVE until `confirmExtract`. A raid
 * launched before rules 2 concludes here as it always did.
 */
export async function extractRaid(): Promise<void> {
  raidStateStore.set((s) => ({ ...s, error: null }));
  try {
    const response = await api.post<RaidStatusResponse>('/raid/extract', {});
    raidStateStore.set((s) => ({
      ...s,
      ...liveFields(response),
      isPolling: response.status === 'ACTIVE',
    }));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to extract raid';
    raidStateStore.set((s) => ({ ...s, error: message }));
    throw err;
  }
}

/** Rules 2: carry the pick home and conclude. The server checks it fits. */
export async function confirmExtract(keep: RaidKeepPick[]): Promise<void> {
  raidStateStore.set((s) => ({ ...s, error: null }));
  try {
    const response = await api.post<RaidStatusResponse>('/raid/extract/confirm', { keep });
    raidStateStore.set((s) => ({ ...s, ...liveFields(response), isPolling: false }));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to extract';
    raidStateStore.set((s) => ({ ...s, error: message }));
    throw err;
  }
}

function parseStart(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

/**
 * Clears the raid back to "no raid" but KEEPS the player's chosen region — that's
 * a UI preference, not raid state, and dropping it would silently snap the picker
 * back to tier 1 after every run.
 */
export function resetRaid(): void {
  raidStateStore.set((s) => ({ ...INITIAL_STATE, selectedRegion: s.selectedRegion }));
}

export function dismissRaidResult(): void {
  raidStateStore.set((s) => ({
    ...s,
    raidId: null,
    status: null,
    outcome: null,
    isPolling: false,
  }));
}
